/**
 * Cookie/consent state — same shape as the `i18n` lang store (a module-level
 * value + subscriber set, persisted to `localStorage`, guarded the same way
 * `storage.ts` guards workspace persistence). `necessary` isn't tracked here:
 * it can't be turned off, so there's nothing to store or gate — only the
 * optional categories below have a boolean to remember.
 *
 * Nothing in the app sends data anywhere yet. This store exists so that when
 * telemetry/crash-reporting is added, it can gate on `hasConsent(...)` from
 * day one instead of retrofitting consent after the fact.
 */

import { safe } from '../lib/safe';

export type ConsentCategory = 'statistics' | 'crashReports';

export const CONSENT_CATEGORIES: readonly ConsentCategory[] = ['statistics', 'crashReports'];

interface ConsentState {
  version: 1;
  decidedAt: string;
  categories: Record<ConsentCategory, boolean>;
}

const STORAGE_KEY = 'kuer.consent.v1';
const VERSION = 1;

function readState(): ConsentState | null {
  return safe(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== VERSION || typeof parsed.categories !== 'object') return null;
    return parsed;
  }, null);
}

let state: ConsentState | null = readState();
type Listener = () => void;
const listeners = new Set<Listener>();

/** Whether the user has made (or previously made) a consent choice at all —
 *  drives whether the first-run banner needs to show. */
export function consentDecided(): boolean {
  return state !== null;
}

export function hasConsent(category: ConsentCategory): boolean {
  return state?.categories[category] ?? false;
}

export function setConsent(categories: Record<ConsentCategory, boolean>): void {
  state = { version: VERSION, decidedAt: new Date().toISOString(), categories: { ...categories } };
  safe(() => localStorage.setItem(STORAGE_KEY, JSON.stringify(state)), undefined);
  for (const fn of listeners) fn();
}

export function acceptAllConsent(): void {
  setConsent(Object.fromEntries(CONSENT_CATEGORIES.map((c) => [c, true])) as Record<ConsentCategory, boolean>);
}

export function acceptNecessaryOnly(): void {
  setConsent(Object.fromEntries(CONSENT_CATEGORIES.map((c) => [c, false])) as Record<ConsentCategory, boolean>);
}

export function onConsentChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
